import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";
import { useTranslation } from "@coworker/locales";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Navigate } from "react-router";
import { FilterPanel } from "../../shared/filters/components/FilterPanel";
import Linkbar from "../../shared/components/Linkbar/Linkbar";
import FixaButton from "../../shared/wrappers/FixaButton/FixaButton";
import FixaInlineMessage from "../../shared/wrappers/FixaInlineMessage/FixaInlineMessage";
import { FixaText } from "../../shared/wrappers/FixaText/FixaText";
import { LaunchMediaUploadModal } from "../components/LaunchMediaUploadModal/LaunchMediaUploadModal";
import { LaunchMediaSection } from "../components/LaunchMediaSection/LaunchMediaSection";
import {
  ACCEPTED_ARTICLE_FILE_EXTENSIONS,
  ACCEPTED_ARTICLE_MIME_TYPES,
  ACCEPTED_FILE_EXTENSIONS,
  ACCEPTED_MIME_TYPES,
} from "../config";
import { useLaunchMediaFilters } from "../hooks/useLaunchMediaFilters";
import { useAllLaunchMedias } from "../hooks/useLaunchMedias";
import { useLaunches } from "../hooks/useLaunches";
import type { Launch } from "../types/launch";
import styles from "./launch-media-page.module.css";

const EMPTY_LAUNCHES: Launch[] = [];

const LaunchMediaPage = () => {
  const { t } = useTranslation();
  const [uploadIsOpen, setUploadIsOpen] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState<string>();

  const queryClient = useQueryClient();

  const enableLaunchMedia = useFlag(FLAGS.ENABLE_LAUNCH_MEDIA);
  const { data: launches = EMPTY_LAUNCHES } = useLaunches();
  const { mediasByLaunchId } = useAllLaunchMedias(launches);
  const { useFiltersFactory, filterValues, setFilterValues, sections } =
    useLaunchMediaFilters(launches, mediasByLaunchId);

  if (!enableLaunchMedia) {
    return <Navigate to="/home" replace />;
  }

  return (
    <main className={styles["page"]}>
      <Linkbar currentLabel={t("capitalCaseLaunchMediaString")} />

      <FixaText tagName="h1" textStyle="Heading.L" className={styles["title"]}>
        {t("capitalCaseLaunchMediaString")}
      </FixaText>

      <div className={styles["filters-row"]}>
        <div className={styles["filters"]}>
          <FilterPanel
            feature="launchMedia"
            filterValues={filterValues}
            setFilterValues={setFilterValues}
            useFiltersFactory={useFiltersFactory}
          />
        </div>

        <div className={styles["actions"]}>
          <FixaButton
            className={styles["trigger"]}
            htmlType="button"
            onClick={() => {
              setConfirmationMessage(undefined);
              setUploadIsOpen(true);
            }}
            text={t("uploadString")}
            type="emphasised"
          />
          <LaunchMediaUploadModal
            isOpen={uploadIsOpen}
            onClose={() => setUploadIsOpen(false)}
            onConfirmed={() => {
              setConfirmationMessage("Media import was saved successfully.");
              void queryClient.invalidateQueries({
                queryKey: ["launch-media", "launches"],
              });
            }}
            presentationConstraints={{
              acceptedFileExtensions: ACCEPTED_FILE_EXTENSIONS,
              acceptedMimeTypes: ACCEPTED_MIME_TYPES,
            }}
            articleConstraints={{
              acceptedFileExtensions: ACCEPTED_ARTICLE_FILE_EXTENSIONS,
              acceptedMimeTypes: ACCEPTED_ARTICLE_MIME_TYPES,
            }}
          />
        </div>
      </div>

      {confirmationMessage && (
        <div role="status">
          <FixaInlineMessage
            variant="positive"
            title={<FixaText>Media saved</FixaText>}
            body={<FixaText>{confirmationMessage}</FixaText>}
          />
        </div>
      )}

      {sections.map(({ launch, medias }) => (
        <LaunchMediaSection key={launch.id} launch={launch} medias={medias} />
      ))}
    </main>
  );
};

export default LaunchMediaPage;
